import { supabase } from './supabase';
import type { Agent } from './types';

/** A failed call, with the machine-readable reason when the server gave one. */
export class FunctionError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number,
  ) {
    super(message);
    this.name = 'FunctionError';
  }
}

/** POSTs to one of our edge functions as the signed-in user. */
export async function callFunction<T>(
  name: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('User not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'X-User-Token': session.access_token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new FunctionError(
      data.error || 'Request failed',
      data.code,
      response.status,
    );
  }
  return data as T;
}

/**
 * Agents that live outside PromptSafe (Claude Console, the customer's own
 * API). Their prompt isn't ours: they're read-only here, can't be cloned,
 * and never see the simulated opening message as their own.
 */
export const isConnectedAgent = (agent?: Pick<Agent, 'source'> | null) =>
  Boolean(agent?.source && agent.source !== 'promptsafe');
